/**
 * @namespace Stores
 * @category Stores
 * @module zones-store
 * */

import api from '@/api'
import { getNestedValue } from '@/utils/object.js'
import Vue from 'vue'
import APIUrls from '@/config/simpliciti-apis.js'
import { normalizeZoneItemPolygon } from '@/services/map-service.js'

function newState() {
  return {
    isSyncInProgress: false,
    zones: [],
    zoneTypes: [],
  }
}
export default {
  namespaced: true,
  state: newState(),
  getters: {
    allClientZones: (state) => state.zones,
    clientZones: (state) =>
      state.zones.filter((zone) => {
        let matchedZoneType = state.zoneTypes.find(
          (zoneType) => zoneType.id === zone.categoryId
        )
        return !!matchedZoneType && matchedZoneType.enabled
      }),
    clientZoneTypes: (state) => state.zoneTypes,
  },
  mutations: {
    resetStore(state) {
      Object.assign(state, newState())
    },
    isSyncInProgress: (state, value) => (state.isSyncInProgress = value),
    zones: (state, value = []) =>
      (state.zones = value.map((zone) => {
        zone.category = state.zoneTypes.find(
          (type) => type.id.toString() === zone.categoryId.toString()
        )
        return zone
      })).sort((a, b) => {
        return a.name < b.name ? -1 : 1
      }),
    zoneTypes: (state, value = []) => (state.zoneTypes = value),
    toggleZoneType(state, { id, value }) {
      let index = state.zoneTypes.findIndex((type) => type.id === id)
      if (index >= 0) {
        state.zoneTypes[index].enabled = value
      }
    },
  },
  actions: {
    resetStore({ commit }) {
      commit('resetStore')
    },

    toggleZoneType({ commit }, { id, value }) {
      commit('toggleZoneType', { id, value })
    },

    async syncClientZonesTypes({ state, commit, dispatch, rootGetters }) {
      //Skip if already loaded
      if (state.zoneTypes.length > 0) {
        return
      }

      //Get enabledClientZones from cache (User can toggle on/off certain zone types)
      await dispatch(
        'settings/syncFromCache',
        { once: true },
        {
          root: true,
        }
      )
      let enabledClientZones =
        rootGetters['settings/getParameter']('enabledClientZones')

      let data = (
        await api.v2.get(
          `/v2/typezoneclient`,
          {},
          {
            jwt: rootGetters['auth/jwt'], //Hot-fix for: request doesn't send JWT
          }
        )
      ).data
      commit(
        'zoneTypes',
        data.map((item) => {
          /*
         "id": 6568,
        "nom": "Carrière",
        "couleur": "cccccc",
        "image": "drapeau_rouge_32x32.gif",
        "vidage": false
        */
          let settingItem = enabledClientZones.find((i) => i.id === item.id)
          return {
            id: getNestedValue(item, 'id'),
            name: getNestedValue(item, 'nom'),
            color: getNestedValue(item, 'couleur'),
            enabled: settingItem ? settingItem.enabled : false,
          }
        })
      )
    },
    /**
     * Fetch Client zones from APIV2 into Vuex
     * @param {Object} options.force Will force fetch when map zones are disabled or zones are already loaded
     */
    async syncClientZones(
      { rootGetters, dispatch, commit, state },
      options = {}
    ) {
      options = options || {}
      await dispatch(
        'settings/syncFromCache',
        {
          once: true,
        },
        {
          root: true,
        }
      )
      const shouldSyncZones = rootGetters['settings/getParameter'](
        'areClientZonesEnabledOnTheMap'
      )

      if (!shouldSyncZones && options.force !== true) {
        return
      }

      //Load zones once
      if (
        state.isSyncInProgress ||
        (state.zones.length > 0 && options.force !== true)
      ) {
        return
      }

      commit('isSyncInProgress', true)
      await dispatch('syncClientZonesTypes')
      let data = (await api.v2.get(APIUrls.APIV2_ZONE_CLIENT)).data
      commit('isSyncInProgress', false)
      let invalidZonesCount = 0
      data = data.map(normalizeZoneItem).filter((zone) => {
        let isValidZone =
          (zone.type === 'P' && zone.polygon !== undefined) ||
          (zone.type === 'R' && !!zone.radius)
        invalidZonesCount += isValidZone ? 0 : 1
        return isValidZone
      })
      if (invalidZonesCount.length > 0) {
        Vue.$log.warn(`${invalidZonesCount} invalid zones skiped`)
      }
      commit('zones', data)
    },
  },
}

function normalizeZoneItem(item) {
  /*
    {
  "id": 6712,
  "nom": "Parking",
  "x": 4.8782,
  "y": 43.95145,
  "polygone": "(487818,4395235):(487816,4395235)",
  "classe": 10101,
  "rayon": 100,
  "type_zone": "R",
  "acces_rrx": 0,
  "acces_rry": 0,
  "interne": -1,
  "categoriezone_id": 1524,
  "categoriezone_nom": "Divers 2",
  "categoriezone_couleur": "b7e8b5",
  "categoriezone_image": "drapeau_vert_32x32.gif"
}
*/
  const type = getNestedValue(item, 'type_zone')
  return {
    id: getNestedValue(item, 'id'),
    name: getNestedValue(item, 'nom'),
    type,
    polygon:
      type === 'P'
        ? normalizeZoneItemPolygon(getNestedValue(item, 'polygone'))
        : undefined,
    radius: type === 'R' ? getNestedValue(item, 'rayon') : undefined,
    lat: getNestedValue(item, 'y'),
    lng: getNestedValue(item, 'x'),
    access_lat: getNestedValue(item, 'acces_rry'),
    access_lng: getNestedValue(item, 'acces_rrx'),
    categoryId: getNestedValue(item, 'categoriezone_id'),
    categoryColor: getNestedValue(item, 'categoriezone_couleur', 'green', {
      transform(value) {
        return '#' + value.split('#').join('')
      },
    }),
    categoryImageName: getNestedValue(
      item,
      'categoriezone_image',
      'drapeau_bleu_32x32.gif'
    ),
  }
}
